import { type JSX,useEffect, useState } from 'react';

import { Autocomplete,FormControl, MenuItem, TextField } from '@mui/material';
import { AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch } from 'app/hooks';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { bindFilterHelper, filterHelper, INamedEntity } from '../Common/helpers';
import { CustomPaperComponent, DISPLAYED_ROWS_COUNT } from '../CustomPaperComponent';

const AUTOCOMPLETE_ERROR_INLINE_STYLES = {
    backgroundColor: 'error.light',
    borderRadius: '4px',
    border: 'solid 1px',
    borderColor: 'error.main'
};

const AUTOCOMPLETE_INLINE_STYLES = {
    color: 'text.secondary'
};

const entityIdProperty = 'id';

interface EntitiesSearchableCommonDropDownProps<T extends INamedEntity, A> {
    initialEntityOption: T,
    onChange?: (event: unknown, value: T | null) => void;
    value?: T | null;
    label: string;
    getOptionLabel: (option: T) => string;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    asyncThunkAction: AsyncThunk<T[], A, Record<string, unknown>>;
    asyncThunkArgs: A;
    displayMenuItemContent: (option: T) => JSX.Element;
    filterOptionsCustomCallback?: (option: T, input: string) => boolean;
    shouldUseCustomPaperComponent?: boolean;
    hasError?: boolean;
    effectDependecyArray?: unknown[];
    disabled?: boolean;
    defaultEntityValue?: T | null;
    entityPlaceholder?: string;
    defaultNameForSettingDefaultEntityValue?: string;
}

export function EntitiesSearchableCommonDropDown<T extends INamedEntity, A>({
    initialEntityOption,
    value,
    onChange,
    hasError,
    label,
    getOptionLabel,
    isOptionEqualToValue,
    asyncThunkAction,
    asyncThunkArgs,
    displayMenuItemContent,
    shouldUseCustomPaperComponent = true,
    filterOptionsCustomCallback,
    effectDependecyArray = [],
    disabled = false,
    defaultEntityValue = null,
    entityPlaceholder,
    defaultNameForSettingDefaultEntityValue
}: Readonly<EntitiesSearchableCommonDropDownProps<T, A>>) {
    const [entities, setEntities] = useState<T[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);
    const [defaultEntityValueByName, setDefaultEntityValueByName] = useState<T | null>(null);
    const [defaultValueForEntity, setDefaultValueForEntity] = useState<T | null>(defaultEntityValue);
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const autoCompleteErrorInlineStyles = hasError ? AUTOCOMPLETE_ERROR_INLINE_STYLES: AUTOCOMPLETE_INLINE_STYLES;
    const customPaperComponentToRender = shouldUseCustomPaperComponent ? CustomPaperComponent : undefined;

    const filterOptionsCallback = filterOptionsCustomCallback ?
        bindFilterHelper(filterOptionsCustomCallback) : filterHelper as ((options: T[], state: { inputValue: string }) => T[]);

    // Default comparison by id - this fixes MUI Autocomplete warnings about mismatched options
    const isOptionEqualToValueCallback = isOptionEqualToValue ?? ((option: T, value: T) => option.id === value.id);

    const _setDefaultValueByProvidedName = (entities: T[]) => {
        const foundDefaultEntityValue = entities?.find(entity => entity.name === defaultNameForSettingDefaultEntityValue);
        if(foundDefaultEntityValue) {
            setDefaultEntityValueByName(foundDefaultEntityValue);
            onChange?.(null, foundDefaultEntityValue);
        }
    };

    useEffect(() => {
        if(defaultEntityValue && !disabled) {
            setDefaultValueForEntity(defaultEntityValue);
            onChange?.(null, defaultEntityValue);
        }
    }, []);

    useEffect(() => {
        if (!disabled ) {
            setProcessing(true);

            dispatch(asyncThunkAction(asyncThunkArgs as A & undefined))
                .unwrap()
                .then((response) => {
                    if(defaultNameForSettingDefaultEntityValue) {
                        setEntities(response);
                        setProcessing(false);
                        _setDefaultValueByProvidedName(response);
                    } else {
                        setEntities(response);
                        setProcessing(false);
                    }
                })
                .catch(error => {
                    setEntities([]);
                    setProcessing(false);
                    handleListFetchError(extractStatusCode(error.message), label, enqueueSnackbar);
                })
                .finally(() => {
                    setProcessing(false);
                });
        }
    }, effectDependecyArray);

    return (
        <FormControl>
            <Autocomplete
                defaultValue={defaultValueForEntity ?? defaultEntityValueByName}
                disabled={disabled}
                PaperComponent={customPaperComponentToRender}
                sx={autoCompleteErrorInlineStyles}
                disableClearable={!!value}
                loading={processing}
                handleHomeEndKeys={false}
                options={[initialEntityOption, ...entities]}
                value={value}
                onChange={onChange}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValueCallback}
                filterOptions={filterOptionsCallback}
                renderInput={(params) => <TextField {...params} label={label} placeholder={entityPlaceholder} />}
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                renderOption={({ key: _, ...params }, option) => {
                    const entityId = String(option[entityIdProperty as keyof T]);

                    if (params.id && Number(params.id.split('-').pop()) > DISPLAYED_ROWS_COUNT) {
                        return null;
                    }

                    return (
                        <MenuItem
                            key={entityId}
                            {...params}
                            value={entityId}
                            style={entityId ? { display: 'block' } : { display: 'none' } }
                        >
                            {displayMenuItemContent(option)}
                        </MenuItem>
                    );
                }}
            />
        </FormControl>

    );
}
